import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Envelope, Lock, Eye, EyeSlash, GoogleLogo, AppleLogo } from '@phosphor-icons/react';
import { API } from '@/App';
import { toast } from 'sonner';
import DropioLogo from '@/components/DropioLogo';
import InAppBrowserBlockerModal from '@/components/InAppBrowserBlockerModal';
import useInAppBrowser from '@/hooks/useInAppBrowser';

const LoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [blockerOpen, setBlockerOpen] = useState(false);
  const inApp = useInAppBrowser();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        const msg = typeof detail === 'string' ? detail : 'Login failed';
        throw new Error(msg);
      }
      toast.success(`Welcome back, ${data.name?.split(' ')[0] || 'friend'}!`);
      navigate('/dashboard', { state: { user: data } });
    } catch (err) {
      toast.error(err.message);
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // In-app browsers (Instagram/Facebook/Messenger/TikTok) get Google's
  // `Error 403: disallowed_useragent` → intercept the click and guide the user
  // to their default OS browser.
  const handleGoogleLogin = () => {
    if (inApp.isInApp) {
      setBlockerOpen(true);
      return;
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/" data-testid="login-logo-link"><DropioLogo size={48} /></Link>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="login-heading">
              Welcome back
            </h1>
            <p className="text-sm text-gray-600">Log in to your Dropio account</p>
          </div>

          {/* In-app browser warning banner — surfaces the issue proactively */}
          {inApp.isInApp && (
            <div
              className="mb-5 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-900 flex items-start gap-2"
              data-testid="inapp-banner"
              role="alert"
            >
              <span aria-hidden>⚠️</span>
              <div>
                You&apos;re viewing this inside <strong>{inApp.appName === 'WebView' ? 'an embedded browser' : inApp.appName}</strong>. Google Sign-In won&apos;t work here.
                <button
                  type="button"
                  onClick={() => setBlockerOpen(true)}
                  className="ml-1 underline font-semibold"
                  data-testid="inapp-banner-open"
                >Open in Safari/Chrome →</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-5">
            <button onClick={handleGoogleLogin} className="flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="google-login">
              <GoogleLogo size={18} weight="bold" /> <span className="text-sm font-medium">Google</span>
            </button>
            <button onClick={handleGoogleLogin} className="flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="apple-login">
              <AppleLogo size={18} weight="fill" /> <span className="text-sm font-medium">Apple</span>
            </button>
          </div>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200"></span></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-400">or continue with email</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Envelope size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                name="email" type="email" placeholder="Email address" value={form.email} onChange={handleChange} required
                className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100"
                data-testid="login-email"
              />
            </div>
            <div className="relative">
              <Lock size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                name="password" type={showPassword ? 'text' : 'password'} placeholder="Password" value={form.password} onChange={handleChange} required
                className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100"
                data-testid="login-password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-[#6D28D9] focus:ring-purple-200" />
                <span className="text-gray-600">Remember me</span>
              </label>
              <a href="#" className="text-[#6D28D9] font-semibold hover:underline">Forgot password?</a>
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full dropio-gradient-cta text-white font-semibold py-3 rounded-lg shadow-lg shadow-purple-200 hover:shadow-xl transition-all disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-[#6D28D9] font-semibold hover:underline" data-testid="goto-signup">Sign Up</Link>
          </p>
        </div>
      </div>
      <InAppBrowserBlockerModal
        open={blockerOpen}
        onClose={() => setBlockerOpen(false)}
        appName={inApp.appName}
        os={inApp.os}
      />
    </div>
  );
};

export default LoginPage;
